import axios from 'axios'
import { useAuthStore } from '../store'
import { createRefreshCoordinator, isRefreshRequest } from './refreshCoordinator'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// Criar instância do Axios
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

const refreshApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

const requestTokenRefresh = (refreshToken) => {
  const { tenant } = useAuthStore.getState()
  const headers = tenant?.id ? { 'X-Tenant-ID': tenant.id } : undefined

  return refreshApi.post(
    '/api/auth/refresh',
    { refreshToken },
    { headers, skipAuthRefresh: true }
  )
}

const refreshCoordinator = createRefreshCoordinator({
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  refresh: async (refreshToken) => {
    const response = await requestTokenRefresh(refreshToken)
    return response.data?.token || response.data?.tokens?.access
  },
  updateToken: (token) => useAuthStore.getState().updateToken(token),
  logout: () => useAuthStore.getState().logout(),
  redirect: () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const { token, tenant } = useAuthStore.getState()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (tenant?.id) {
      config.headers['X-Tenant-ID'] = tenant.id
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshRequest(originalRequest)
    ) {
      originalRequest._retry = true

      try {
        const token = await refreshCoordinator.refreshAccessToken()

        if (typeof originalRequest.headers?.set === 'function') {
          originalRequest.headers.set('Authorization', `Bearer ${token}`)
        } else {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${token}`
          }
        }

        return api(originalRequest)
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// API Services
export const authService = {
  login: (credentials) => api.post('/api/auth/login', credentials, { skipAuthRefresh: true }),
  logout: () => api.post('/api/auth/logout'),
  refresh: (refreshToken) => requestTokenRefresh(refreshToken),
  me: () => api.get('/api/auth/me'),
  changePassword: (data) => api.post('/api/auth/change-password', data),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  switchTenant: (tenantId) => api.post('/api/auth/switch-tenant', { tenantId })
}

export const dashboardService = {
  getMetrics: (params) => api.get('/api/dashboard/metricas', { params }),
  getOverview: (params) => api.get('/api/dashboard/overview', { params }),
  getRealtime: () => api.get('/api/dashboard/tempo-real'),
  getCharts: (params) => api.get('/api/dashboard/graficos', { params }),
  getAlerts: () => api.get('/api/dashboard/alertas')
}

export const activitiesService = {
  list: (params) => api.get('/api/activities', { params }),
  getFilters: () => api.get('/api/activities/filters'),
  getSummary: (params) => api.get('/api/activities/summary', { params }),
  getTimeline: (params) => api.get('/api/activities/timeline', { params }),
  create: (data) => api.post('/api/activities', data),
  getAppUsage: (params) => api.get('/api/analytics/applications', { params }),
  getAppUsageSummary: (params) => api.get('/api/analytics/applications/summary', { params }),
  getAppFilters: () => api.get('/api/analytics/applications/filters')
}

export const analyticsService = {
  getFilters: () => api.get('/api/analytics/filters'),
  getProductivity: (params) => api.get('/api/analytics/productivity', { params }),
  getApplicationUsage: (params) => api.get('/api/analytics/applications', { params }),
  getUserAnalysis: (params) => api.get('/api/analytics/users', { params }),
  getTrends: (params) => api.get('/api/analytics/trends', { params }),
  getPatterns: (userId) => api.get(`/api/analytics/patterns/${userId}`),
  getInternet: (params) => api.get('/api/analytics/internet', { params }),
  getInternetFilters: () => api.get('/api/analytics/internet/filters'),
  getInternetDetails: (host, params) => api.get(`/api/analytics/internet/${encodeURIComponent(host)}/details`, { params }),
  generateReport: (params) => api.post('/api/analytics/report', params),
  getAppUsers: (aplicativo, params) => api.get(`/api/analytics/applications/${encodeURIComponent(aplicativo)}/users`, { params })
}

export const aiService = {
  getInsights: (params) => api.get('/api/ai/insights', { params }),
  analyze: (data) => api.post('/api/ai/analyze', data),
  getRecommendations: (params) => api.get('/api/ai/recommendations', { params }),
  chat: (message) => api.post('/api/ai/chat', { message })
}

export const adminService = {
  // Usuários
  listUsers: (params) => api.get('/api/admin/users', { params }),
  createUser: (data) => api.post('/api/admin/users', data),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  updateUserPassword: (id, password) => api.put(`/api/admin/users/${id}/password`, { senha: password }),
  deleteUser: (id, params) => api.delete(`/api/admin/users/${id}`, { params }),

  // Empresas/Tenants
  listTenants: (params) => api.get('/api/admin/tenants', { params }),
  createTenant: (data) => api.post('/api/admin/tenants', data),
  updateTenant: (id, data) => api.put(`/api/admin/tenants/${id}`, data),
  deleteTenant: (id) => api.delete(`/api/admin/tenants/${id}`),

  // Estatísticas
  getStats: (params) => api.get('/api/admin/stats', { params }),

  // Configuração de importação
  getImportConfig: (tenantId) => api.get(`/api/admin/import-config/${tenantId}`),
  saveImportConfig: (tenantId, data) => api.post(`/api/admin/import-config/${tenantId}`, data),
  deleteImportConfig: (tenantId) => api.delete(`/api/admin/import-config/${tenantId}`),

  // Gestão de usuário-tenant
  getUserTenants: (usuarioId) => api.get(`/api/admin/users/${usuarioId}/tenants`),
  setUserTenants: (usuarioId, tenantIds) => api.post(`/api/admin/users/${usuarioId}/tenants`, { tenantIds }),
  removeUserTenant: (usuarioId, tenantId) => api.delete(`/api/admin/users/${usuarioId}/tenants/${tenantId}`),

  // API Token
  generateApiToken: (tenantId) => api.post('/api/upload/generate-token', { tenantId }),
  getApiTokenInfo: (tenantId) => api.get(`/api/upload/token-info/${tenantId}`)
}

export const colaboradoresService = {
  list: (params) => api.get('/api/colaboradores', { params }),
  get: (usuario_id) => api.get(`/api/colaboradores/${usuario_id}`),
  create: (data) => api.post('/api/colaboradores', data),
  update: (usuario_id, data) => api.put(`/api/colaboradores/${usuario_id}`, data),
  delete: (usuario_id) => api.delete(`/api/colaboradores/${usuario_id}`),
  getDepartamentos: () => api.get('/api/colaboradores/departamentos'),
  getCargos: () => api.get('/api/colaboradores/cargos'),
  importarAtividades: () => api.post('/api/colaboradores/importar-atividades'),
  getValidacao: () => api.get('/api/colaboradores/validacao')
}

export const importService = {
  testConnection: (data) => api.post('/api/import/test-connection', data),
  preview: (data) => api.post('/api/import/preview', data),
  importActivities: (data) => api.post('/api/import/activities', data)
}

export const siteCategoriesService = {
  list: (params) => api.get('/api/site-categories', { params }),
  stats: () => api.get('/api/site-categories/stats'),
  get: (host) => api.get(`/api/site-categories/${host}`),
  create: (data) => api.post('/api/site-categories', data),
  update: (host, data) => api.put(`/api/site-categories/${host}`, data),
  delete: (host) => api.delete(`/api/site-categories/${host}`),
  bulkCreate: (sites) => api.post('/api/site-categories/bulk', { sites }),
  uncategorized: () => api.get('/api/site-categories/uncategorized/list'),
  frequentSites: (params) => api.get('/api/site-categories/frequent-sites/last-month', { params }),
  popularSites: (params) => api.get('/api/site-categories/popular-sites', { params })
}

export const appCategoriesService = {
  list: (params) => api.get('/api/app-categories', { params }),
  stats: () => api.get('/api/app-categories/stats'),
  get: (app) => api.get(`/api/app-categories/${encodeURIComponent(app)}`),
  create: (data) => api.post('/api/app-categories', data),
  update: (app, data) => api.put(`/api/app-categories/${encodeURIComponent(app)}`, data),
  delete: (app) => api.delete(`/api/app-categories/${encodeURIComponent(app)}`),
  bulkCreate: (apps) => api.post('/api/app-categories/bulk', { apps }),
  uncategorized: () => api.get('/api/app-categories/uncategorized/list'),
  frequentApps: (params) => api.get('/api/app-categories/frequent-apps/last-month', { params }),
  popularApps: (params) => api.get('/api/app-categories/popular-apps', { params })
}

export const goalsService = {
  list: (params) => api.get('/api/goals', { params }),
  get: (id) => api.get(`/api/goals/${id}`),
  getForUser: (usuario_id) => api.get(`/api/goals/usuario/${usuario_id}`),
  check: (usuario_id, params) => api.get(`/api/goals/check/${usuario_id}`, { params }),
  create: (data) => api.post('/api/goals', data),
  update: (id, data) => api.put(`/api/goals/${id}`, data),
  delete: (id, params) => api.delete(`/api/goals/${id}`, { params }),
  getDashboardStatus: (params) => api.get('/api/goals/dashboard-status', { params }),
  getHistorico: (params) => api.get('/api/goals/historico', { params }),
  getRankingUsuarios: (params) => api.get('/api/goals/ranking/usuarios', { params }),
  getRankingDepartamentos: (params) => api.get('/api/goals/ranking/departamentos', { params })
}

export const fileOperationsService = {
  list: (params) => api.get('/api/file-operations', { params }),
  getFilters: () => api.get('/api/file-operations/filters'),
  summary: (params) => api.get('/api/file-operations/summary', { params }),
  getByUser: (usuario) => api.get(`/api/file-operations/${usuario}`),
  create: (data) => api.post('/api/file-operations', data)
}

export const reportsService = {
  daily: (params) => api.get('/api/reports/daily', { params }),
  weekly: (params) => api.get('/api/reports/weekly', { params }),
  monthly: (params) => api.get('/api/reports/monthly', { params })
}

export default api
